import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  Stack
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { formatUtcDate, globalDateFormat, useToast } from "@qubit/autoparts";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { copyToClipboard } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectAuth0Profile } from "~/redux/selectors/authSelectors";
import { useCreateApiTokenMutation } from "~/redux/warehouse/apiTokens.hooks";
import { warehouseApi } from "~/redux/warehouse/warehouseApi";

type CreateApiTokenModalProps = {
  onClose: () => void;
  open: boolean;
};
type CreateApiTokenForm = {
  apiTokenName: string;
  expirationDate: Date | null;
};

const CreateApiTokenModal = (props: CreateApiTokenModalProps) => {
  const { onClose, open } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { successToast, errorToast, infoToast } = useToast();

  const [
    createApiToken,
    { isLoading, data: createdApiTokenData, reset: resetApiCreationMutation }
  ] = useCreateApiTokenMutation();
  const defaultExpirationDate = dayjs().utc().add(90, "day").toDate();
  const form = useForm<CreateApiTokenForm>({
    defaultValues: {
      apiTokenName: "",
      expirationDate: defaultExpirationDate
    },
    mode: "onChange"
  });

  const {
    formState: { isValid, errors, isDirty },
    reset: resetForm,
    register,
    watch,
    getValues,
    setValue
  } = form;
  const auth0Roles = useAppSelector(selectAuth0Profile)?.roles || [];

  const handleCloseModal = () => {
    onClose();
    resetForm();
    resetApiCreationMutation();
  };

  const handleCreateApiTokenCb = async () => {
    const { apiTokenName, expirationDate } = getValues();
    if (!apiTokenName || !expirationDate) return;
    try {
      const createApiTokenBody = {
        roles: auth0Roles,
        name: apiTokenName,
        expirationDate: formatUtcDate(expirationDate)
      };

      await createApiToken(createApiTokenBody).unwrap();
      successToast(t("api token creation success"));
      dispatch(warehouseApi.util.invalidateTags(["api tokens"]));
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      onClose={(_e, reason) => {
        if (reason === "backdropClick" && !!createdApiTokenData) return;
        handleCloseModal();
      }}
      disableEscapeKeyDown={!!createdApiTokenData}
    >
      <DialogTitle>{t("create api token")}</DialogTitle>
      <DialogContent sx={{ mx: 3, mt: 3 }}>
        {createdApiTokenData ? (
          <Stack flexDirection="column" gap={5} alignItems="center">
            <Stack flexDirection="column" gap={5} alignItems="center">
              <Box width="100%">
                <Typography fontSize="1.2rem" textAlign="center">
                  {t("api token creation success")}
                </Typography>
                <Typography fontSize="1.2rem" textAlign="center">
                  {t("api token creation warning")}
                </Typography>
              </Box>
              <Stack flexDirection="column" gap={3} width="90%">
                <TextField
                  value={createdApiTokenData.name}
                  label={t("name")}
                  variant="outlined"
                  slotProps={{
                    input: {
                      disabled: true
                    }
                  }}
                />
                <TextField
                  value={createdApiTokenData.token}
                  label={t("api token")}
                  variant="outlined"
                  fullWidth
                  color="info"
                  slotProps={{
                    input: {
                      readOnly: true,
                      endAdornment: (
                        <IconButton
                          aria-label="copy button"
                          onClick={async () => {
                            await copyToClipboard(createdApiTokenData.token);
                            infoToast(t("copy to clipboard"), {
                              duration: 2000
                            });
                          }}
                          edge="end"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      )
                    }
                  }}
                />
              </Stack>
            </Stack>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="primary"
                disabled={isLoading}
              >
                {t("close")}
              </Button>
            </DialogActions>
          </Stack>
        ) : (
          <Stack flexDirection="column" gap={5} alignItems="center">
            <FormProvider {...form}>
              <form autoComplete="off">
                <Stack flexDirection="column" gap={3} mt={2}>
                  <TextField
                    {...register("apiTokenName", {
                      required: true,
                      minLength: {
                        value: 6,
                        message: t("min text length validation message", {
                          minLength: 6
                        })
                      }
                    })}
                    label={t("name")}
                    type="text"
                    error={!!errors.apiTokenName?.message}
                    helperText={errors.apiTokenName?.message}
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "22px"
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "22px",
                        "&.Mui-focused": {
                          fontSize: "initial",
                          top: "5px"
                        }
                      }
                    }}
                  />
                  <DesktopDatePicker
                    slotProps={{
                      textField: {
                        InputLabelProps: {
                          shrink: true
                        },
                        label: t("expiration"),
                        variant: "outlined",
                        fullWidth: true,
                        sx: {
                          ".MuiInputBase-input": {
                            fontSize: "22px"
                          }
                        }
                      }
                    }}
                    label={t("expiration")}
                    format={globalDateFormat}
                    minDate={dayjs().add(2, "week")}
                    {...register("expirationDate", {
                      required: true
                    })}
                    value={
                      watch("expirationDate")
                        ? dayjs(watch("expirationDate"))
                        : null
                    }
                    onChange={(date) => {
                      setValue("expirationDate", date?.toDate() ?? null, {
                        shouldValidate: true
                      });
                    }}
                  />
                </Stack>
              </form>
            </FormProvider>

            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="primary"
                disabled={isLoading}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleCreateApiTokenCb}
                disabled={!isValid || !isDirty}
                color="primary"
                loading={isLoading}
              >
                {t("confirm")}
              </Button>
            </DialogActions>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateApiTokenModal;
